import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { DocumentList } from "@/components/DocumentList";
import { ExtractedDataTable } from "@/components/ExtractedDataTable";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { OutcomeCard } from "@/components/OutcomeCard";
import { Card, Spinner } from "@/components/ui/primitives";
import { markStageForPlayback, useAutoRun } from "@/lib/playback";
import { usePlayback } from "@/hooks/usePlayback";
import { useAutoAdvance } from "@/hooks/useAutoAdvance";

export const Route = createFileRoute("/apps/$id/intake")({ component: IntakePage });

// sessionStorage key for remembering that a driveable intake has already had
// its agents play out. Survives refresh and back-navigation within the tab.
function intakeRanKey(appId: string): string {
  return `intake-ran:${appId}`;
}

function hasIntakeRun(appId: string): boolean {
  try {
    return sessionStorage.getItem(intakeRanKey(appId)) === "1";
  } catch {
    return false;
  }
}

function markIntakeRan(appId: string): void {
  try {
    sessionStorage.setItem(intakeRanKey(appId), "1");
  } catch {
    // ignore
  }
}

function IntakePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["intake", id],
    queryFn: () => api.getIntake(id),
  });
  const { data: app } = useQuery({ queryKey: ["application", id], queryFn: () => api.getApplication(id) });

  const advance = useMutation({
    mutationFn: () => api.advance(id),
    onSuccess: async () => {
      markStageForPlayback(id, "analysis");
      await qc.invalidateQueries();
      navigate({ to: "/apps/$id/analysis", params: { id } });
    },
  });
  const rerun = useMutation({ mutationFn: () => api.reRunStage(id, "intake"), onSuccess: () => qc.invalidateQueries() });

  const isCurrent = app?.stage === "intake";

  // Intake has "run" implicitly if the application has already moved past
  // intake (historical view). Otherwise we gate it on an explicit trigger.
  const historical = Boolean(app && app.stage !== "intake");
  const [hasRun, setHasRun] = useState<boolean>(() => historical || hasIntakeRun(id));

  // If the app moves past intake (e.g. after advance), flip hasRun so the
  // completed state renders on return visits.
  useEffect(() => {
    if (historical && !hasRun) setHasRun(true);
  }, [historical, hasRun]);

  const { isAutoRunning } = useAutoRun();
  const autoRun = isAutoRunning(id);

  // When auto-run kicks in while we're on a fresh intake page, start the
  // three-agent playback automatically. Manual trigger is handled by the
  // button below.
  useEffect(() => {
    if (autoRun && isCurrent && !hasRun) {
      markIntakeRan(id);
      markStageForPlayback(id, "intake");
      setHasRun(true);
    }
  }, [autoRun, isCurrent, hasRun, id]);

  const playback = usePlayback({
    stage: "intake",
    appId: id,
    events: data?.journey_events ?? [],
    enabled: hasRun,
    cardCount: 0,
  });

  useAutoAdvance({
    appId: id,
    stage: "intake",
    isCurrent: Boolean(isCurrent),
    playbackStatus: playback.status,
    advanceMutation: advance,
    nextStage: "analysis",
  });

  if (isLoading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Spinner /> Loading intake…</div>;
  }
  if (!data) return null;

  const avg =
    data.extracted_fields.length === 0
      ? 0
      : data.extracted_fields.reduce((a, b) => a + b.confidence, 0) / data.extracted_fields.length;

  const runIntake = () => {
    markIntakeRan(id);
    markStageForPlayback(id, "intake");
    setHasRun(true);
  };

  // Idle: docs uploaded, agents haven't run yet.
  const idle = isCurrent && !hasRun;
  const showExtracted = playback.revealFindings;
  const showOutcome = playback.revealOutcome;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        <header>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <FileText className="h-5 w-5 text-brand" strokeWidth={1.9} /> Document Intake
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The Reader, Identity Check and Address Verification agents extract structured data from the uploaded documents.
          </p>
        </header>

        <DocumentList documents={data.documents} />

        <section>
          <div className="mb-3 flex items-end justify-between">
            <div>
              <h3 className="text-base font-semibold">Extracted data</h3>
              <p className="text-xs text-muted-foreground">
                {idle || !showExtracted
                  ? "Will appear once the intake agents have run."
                  : `${data.extracted_fields.length} fields extracted from uploaded documents`}
              </p>
            </div>
          </div>

          {idle ? (
            <Card className="flex items-center gap-3 p-5 text-sm text-muted-foreground">
              <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.9} />
              <div>
                <div className="font-semibold text-foreground">Awaiting extraction</div>
                <div className="text-xs">
                  Run the intake agents to extract customer, employment and address data from the uploaded documents.
                </div>
              </div>
            </Card>
          ) : !showExtracted ? (
            <Card className="flex items-center gap-3 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-brand" strokeWidth={2.2} />
              Intake agents are reading the documents…
            </Card>
          ) : (
            <ExtractedDataTable fields={data.extracted_fields} />
          )}
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold">Agent activity</h3>
          {idle ? (
            <Card className="p-4 text-sm text-muted-foreground">
              No agents have run yet.
            </Card>
          ) : (
            <JourneyTimeline events={data.journey_events} playback={playback} />
          )}
        </section>
      </div>

      {idle ? (
        <OutcomeCard
          tone="warn"
          title="Ready to extract"
          primaryLabel="Run intake extraction"
          onPrimary={runIntake}
        >
          <BulletStat label={`${data.documents.length} documents uploaded`} />
          <BulletStat label="Reader Agent standing by" />
          <BulletStat label="Identity & Address checks queued" />
        </OutcomeCard>
      ) : !showOutcome ? (
        <aside className="sticky top-8 rounded-xl border border-brand/30 bg-brand/5 p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-brand">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
            Running intake…
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Reader, Identity Check and Address Verification agents are processing
            the uploaded documents. Extracted fields will appear once all three
            have finished.
          </p>
        </aside>
      ) : (
        <OutcomeCard
          tone="success"
          title="Extraction Complete"
          primaryLabel={isCurrent ? "Continue to Analysis →" : "Go to Analysis →"}
          onPrimary={() => (isCurrent ? advance.mutate() : navigate({ to: "/apps/$id/analysis", params: { id } }))}
          primaryDisabled={advance.isPending}
          secondaryLabel={isCurrent ? "Re-run extraction" : undefined}
          onSecondary={isCurrent ? () => rerun.mutate() : undefined}
        >
          <BulletStat label={`${data.extracted_fields.length} fields extracted`} />
          <BulletStat label={`${Math.round(avg * 100)}% average confidence`} />
          <BulletStat label={`${data.documents.length} documents processed`} />
        </OutcomeCard>
      )}
    </div>
  );
}

function BulletStat({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full bg-success" />
      <span>{label}</span>
    </div>
  );
}
